import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { type WidgetLayout, defaultDesktopWidgets, defaultClassicWidgets, migrateFromLegacySettings } from '@/lib/widgets'
import type { WorkspaceLayoutPreset, HeaderShortcutData, DashboardPage, LayoutPresetWidget } from '@sevfy/shared'
import { api } from '@/lib/api'
import { DEFAULT_NAV_ORDER } from '@/lib/navItems'
import { useDashboardPages } from '@/composables/useDashboardPages'

/**
 * A quick-access link button pinned to a page header.
 * Matches HeaderShortcutData from @sevfy/shared; re-exported for convenience.
 */
export type HeaderShortcut = HeaderShortcutData

export type { WorkspaceLayoutPreset }

export interface LayoutSidebar {
  position: 'left' | 'right'
  collapsed: boolean
  width: number
}

export interface PanelRoute {
  name: string
  params?: Record<string, string>
  query?: Record<string, string>
  label?: string
}

export interface LayoutSplitView {
  enabled: boolean
  split: number        // 0–100, percentage width for the primary panel
  secondaryRoute: PanelRoute | null
  secondaryTitle: string | null  // display title for the secondary panel header
}

export interface LayoutDashboard {
  widgets: WidgetLayout[]
}

/** Persisted shape: separate widget lists per mode */
export interface LayoutDashboards {
  classic: LayoutDashboard
  desktop: LayoutDashboard
}

export interface LayoutNav {
  /** Ordered list of top-level nav item IDs (user-customized). */
  order: string[]
  /** IDs the user has explicitly hidden. Permanent items cannot be hidden. */
  hidden: string[]
}

export interface LayoutState {
  mode: 'classic' | 'desktop'
  sidebar: LayoutSidebar
  splitView: LayoutSplitView
  dashboards: LayoutDashboards
  /** @deprecated - single dashboard for backwards compat during migration */
  dashboard?: LayoutDashboard
  nav: LayoutNav
  /** Per-page header shortcut buttons. Key = Vue Router route name. */
  headerShortcuts: Record<string, HeaderShortcut[]>
}

export type { WidgetLayout }

const STORAGE_KEY_PREFIX = 'sevfy_layout_v2'  // v2: 40px row grid (was 80px)
const WORKSPACE_ID_KEY = 'sevfy.activeWorkspaceId'

/** Returns the workspace-scoped storage key, or the global key as fallback. */
function storageKey(): string {
  const wsId = localStorage.getItem(WORKSPACE_ID_KEY)
  return wsId ? `${STORAGE_KEY_PREFIX}_${wsId}` : STORAGE_KEY_PREFIX
}

const defaultState: LayoutState = {
  mode: 'classic',
  sidebar: {
    position: 'left',
    collapsed: false,
    width: 220,
  },
  splitView: {
    enabled: false,
    split: 50,
    secondaryRoute: null,
    secondaryTitle: null,
  },
  dashboards: {
    classic: { widgets: [] },
    desktop: { widgets: [] },
  },
  nav: {
    order: [...DEFAULT_NAV_ORDER],
    hidden: [],
  },
  headerShortcuts: {},
}

export const useLayoutStore = defineStore('layout', () => {
  const mode = ref<'classic' | 'desktop'>(defaultState.mode)
  const sidebar = ref<LayoutSidebar>({ ...defaultState.sidebar })
  const splitView = ref<LayoutSplitView>({ ...defaultState.splitView })
  const classicDashboard = ref<LayoutDashboard>({ widgets: [] })
  const desktopDashboard = ref<LayoutDashboard>({ widgets: [] })
  /** Mode-aware accessor - returns the widget list for the current mode. */
  const dashboard = computed({
    get: () => mode.value === 'desktop' ? desktopDashboard.value : classicDashboard.value,
    set: (v: LayoutDashboard) => {
      if (mode.value === 'desktop') desktopDashboard.value = v
      else classicDashboard.value = v
    },
  })
  const nav = ref<LayoutNav>({ ...defaultState.nav, order: [...defaultState.nav.order], hidden: [] })
  const headerShortcuts = ref<Record<string, HeaderShortcut[]>>({})

  function load() {
    try {
      const key = storageKey()
      // Migrate from global key to workspace-scoped key on first load
      let saved = localStorage.getItem(key)
      if (!saved && key !== STORAGE_KEY_PREFIX) {
        const global = localStorage.getItem(STORAGE_KEY_PREFIX)
        if (global) {
          saved = global
          localStorage.setItem(key, global)
          localStorage.removeItem(STORAGE_KEY_PREFIX)
        }
      }
      if (!saved) return
      const parsed = JSON.parse(saved) as Record<string, any>
      if (parsed.mode === 'classic' || parsed.mode === 'desktop') {
        mode.value = parsed.mode
      }
      if (parsed.sidebar) {
        sidebar.value = { ...defaultState.sidebar, ...parsed.sidebar }
      }
      if (parsed.splitView) {
        splitView.value = {
          ...defaultState.splitView,
          split: parsed.splitView.split ?? defaultState.splitView.split,
        }
      }
      // Per-mode dashboards (new format)
      if (parsed.dashboards) {
        if (parsed.dashboards.classic?.widgets?.length) {
          classicDashboard.value = { widgets: parsed.dashboards.classic.widgets }
        }
        if (parsed.dashboards.desktop?.widgets?.length) {
          desktopDashboard.value = { widgets: parsed.dashboards.desktop.widgets }
        }
      } else if (parsed.dashboard?.widgets?.length) {
        // Migrate from old single-dashboard format
        desktopDashboard.value = { widgets: parsed.dashboard.widgets }
        // For classic, remap to compact colSpans (old format used desktop widths)
        const classicDefaults = defaultClassicWidgets()
        classicDashboard.value = {
          widgets: parsed.dashboard.widgets.map((w: any) => {
            const cd = classicDefaults.find(d => d.widgetId === w.widgetId)
            return cd
              ? { ...w, col: cd.col, row: cd.row, colSpan: cd.colSpan, rowSpan: cd.rowSpan }
              : w
          }),
        }
      }
      if (parsed.nav) {
        nav.value = {
          order: Array.isArray(parsed.nav.order) ? parsed.nav.order : [...DEFAULT_NAV_ORDER],
          hidden: Array.isArray(parsed.nav.hidden) ? parsed.nav.hidden : [],
        }
      }
      if (parsed.headerShortcuts && typeof parsed.headerShortcuts === 'object') {
        headerShortcuts.value = parsed.headerShortcuts
      }
      // appliedPresetId / isPresetModified were removed - ignore stale localStorage values
    } catch {
      // ignore malformed storage
    }
  }

  function save() {
    try {
      localStorage.setItem(storageKey(), JSON.stringify({
        mode: mode.value,
        sidebar: sidebar.value,
        splitView: { split: splitView.value.split },
        dashboards: {
          classic: { widgets: classicDashboard.value.widgets },
          desktop: { widgets: desktopDashboard.value.widgets },
        },
        nav: nav.value,
        headerShortcuts: headerShortcuts.value,
      }))
    } catch {
      // ignore storage errors
    }
  }

  // ── Backend sync (nav, shortcuts, core dashboard) ───────────────────────────
  // These fields are stored in User.preferences on the backend so they survive
  // across devices. localStorage remains the primary source on initial load;
  // backend is a secondary sync that runs after mutations (debounced) and on
  // explicit hydrateFromBackend() calls (e.g. after workspace switch).

  let _syncTimer: ReturnType<typeof setTimeout> | null = null

  // True when local state has mutations not yet confirmed by the backend.
  // hydrateFromBackend() skips its update while this is set, preventing the
  // race condition where navigating to DashboardView fetches stale backend
  // state and overwrites pending changes before the debounce fires.
  let _localDirty = false

  function _scheduleBackendSync() {
    _localDirty = true
    if (_syncTimer) clearTimeout(_syncTimer)
    _syncTimer = setTimeout(_syncToBackend, 1500)
  }

  async function flushBackendSync() {
    if (_syncTimer) {
      clearTimeout(_syncTimer)
      _syncTimer = null
    }
    if (_localDirty) await _syncToBackend()
  }

  async function _syncToBackend() {
    try {
      const { pages, hasLoaded } = useDashboardPages()
      await api.patch('/dashboards/settings', {
        activeState: {
          mode: mode.value,
          navOrder: nav.value.order,
          navHidden: nav.value.hidden,
          headerShortcuts: headerShortcuts.value,
          classicDashboard: { widgets: classicDashboard.value.widgets },
          desktopDashboard: { widgets: desktopDashboard.value.widgets },
          // Only include extraPages when pages have been loaded - avoids overwriting
          // backend data with an empty array on first sync before fetchPages() runs.
          ...(hasLoaded.value && pages.value.length > 0 && { extraPages: pages.value }),
        },
      }, { suppressAlert: true })
      _localDirty = false
    } catch {
      // non-fatal - localStorage is the fallback
    }
  }

  /**
   * Load nav layout, shortcuts and core dashboard widgets from the backend.
   * Call this after a workspace switch to get the user's cross-device settings.
   * Backend values take precedence over whatever is in localStorage.
   */
  async function hydrateFromBackend(): Promise<void> {
    // Backend always wins on an explicit hydration call.
    // _localDirty guard was removed: apply-preset now syncs immediately so backend
    // is up-to-date before the next hydration could overwrite it.
    try {
      const data = await api.get<{
        activeState: {
          mode?: 'classic' | 'desktop'
          classicDashboard?: { widgets: WidgetLayout[]; updatedAt: string }
          desktopDashboard?: { widgets: WidgetLayout[]; updatedAt: string }
          navOrder?: string[]
          navHidden?: string[]
          headerShortcuts?: Record<string, HeaderShortcut[]>
          extraPages?: DashboardPage[]
        } | null
        backupState: Record<string, unknown> | null
      }>('/dashboards/settings', { suppressAlert: true })

      const active = data.activeState
      if (!active) return

      // Mode must be hydrated first - setMode initialises an empty widget list if needed
      if (active.mode) {
        mode.value = active.mode
      }
      if (active.navOrder) nav.value.order = active.navOrder
      if (active.navHidden) nav.value.hidden = active.navHidden
      if (active.headerShortcuts) headerShortcuts.value = active.headerShortcuts
      if (active.classicDashboard?.widgets?.length) {
        classicDashboard.value = { widgets: active.classicDashboard.widgets }
      }
      if (active.desktopDashboard?.widgets?.length) {
        desktopDashboard.value = { widgets: active.desktopDashboard.widgets }
      }
      if (active.extraPages?.length) {
        const { pages } = useDashboardPages()
        if (pages.value.length > 0) {
          const incomingMap = new Map(active.extraPages.map(p => [p.id, p]))
          pages.value = pages.value.map(page => incomingMap.get(page.id) ?? page)
        } else {
          pages.value = active.extraPages
        }
      }

      // Write hydrated state back to localStorage so next page load is fast
      save()
    } catch {
      // non-fatal
    }
  }

  function setMode(m: 'classic' | 'desktop') {
    mode.value = m
    // Lazily initialise the target mode's dashboard if it is empty
    const target = m === 'desktop' ? desktopDashboard : classicDashboard
    if (target.value.widgets.length === 0) {
      target.value = {
        widgets: m === 'desktop' ? defaultDesktopWidgets() : defaultClassicWidgets(),
      }
    }
    save()
    // Mode must be persisted to backend - without this, navigating to /d/:slug
    // remounts the component, hydrateFromBackend() fires, and pulls the old mode.
    _scheduleBackendSync()
  }

  function setSidebarCollapsed(v: boolean) {
    sidebar.value.collapsed = v
    save()
  }

  function setSidebarWidth(w: number) {
    sidebar.value.width = Math.max(160, Math.min(400, w))
    save()
  }

  function setSidebarPosition(p: 'left' | 'right') {
    sidebar.value.position = p
    save()
  }

  function setSplitViewEnabled(v: boolean) {
    splitView.value.enabled = v
    if (!v) splitView.value.secondaryRoute = null
    save()
  }

  function setSplitViewSplit(pct: number) {
    splitView.value.split = Math.max(20, Math.min(80, pct))
    save()
  }

  function setSecondaryRoute(route: PanelRoute | null) {
    splitView.value.secondaryRoute = route
    if (route && !splitView.value.enabled) {
      splitView.value.enabled = true
    }
  }

  function setSecondaryTitle(title: string | null) {
    splitView.value.secondaryTitle = title
  }

  /**
   * Initialise the dashboard widget list for the current mode.
   * If widgets are already loaded (from localStorage), this is a no-op.
   * On first use it migrates from the legacy dashboardStore boolean settings.
   */
  function initDashboard(legacySettings?: Record<string, boolean>) {
    if (dashboard.value.widgets.length > 0) return
    if (legacySettings) {
      // Legacy settings only map to the current mode
      dashboard.value = { widgets: migrateFromLegacySettings(legacySettings) }
    } else {
      dashboard.value = {
        widgets: mode.value === 'desktop' ? defaultDesktopWidgets() : defaultClassicWidgets(),
      }
    }
    save()
  }

  function updateWidget(widgetId: string, changes: Partial<WidgetLayout>, persist = true) {
    const idx = dashboard.value.widgets.findIndex(w => w.widgetId === widgetId)
    if (idx === -1) return
    dashboard.value.widgets[idx] = { ...dashboard.value.widgets[idx], ...changes }
    if (persist) {
      save()
      _scheduleBackendSync()
    }
  }

  function reorderWidgets(orderedIds: string[]) {
    orderedIds.forEach((id, idx) => {
      const w = dashboard.value.widgets.find(w => w.widgetId === id)
      if (w) w.order = idx
    })
    save()
    _scheduleBackendSync()
  }

  function resetDashboard() {
    dashboard.value = {
      widgets: mode.value === 'desktop' ? defaultDesktopWidgets() : defaultClassicWidgets(),
    }
    save()
    _scheduleBackendSync()
  }

  function saveDashboard() {
    save()
    _scheduleBackendSync()
  }

  // ── Nav ordering / visibility ────────────────────────────────────────────────

  function setNavOrder(order: string[]) {
    nav.value.order = order
    save()
    _scheduleBackendSync()
  }

  function hideNavItem(id: string) {
    if (!nav.value.hidden.includes(id)) {
      nav.value.hidden = [...nav.value.hidden, id]
      save()
      _scheduleBackendSync()
    }
  }

  function showNavItem(id: string) {
    nav.value.hidden = nav.value.hidden.filter(h => h !== id)
    save()
    _scheduleBackendSync()
  }

  function resetNav() {
    nav.value = { order: [...DEFAULT_NAV_ORDER], hidden: [] }
    save()
    _scheduleBackendSync()
  }

  // ── Workspace layout presets ────────────────────────────────────────────────

  const workspacePresets = ref<WorkspaceLayoutPreset[]>([])
  const presetsLoading = ref(false)

  async function fetchWorkspacePresets(workspaceId: string) {
    presetsLoading.value = true
    try {
      const data = await api.get<{ presets: WorkspaceLayoutPreset[] }>(
        `/auth/tenants/${workspaceId}/layout-presets`
      )
      workspacePresets.value = data.presets
    } catch {
      workspacePresets.value = []
    } finally {
      presetsLoading.value = false
    }
  }

  async function publishPreset(workspaceId: string, name: string, description?: string) {
    // _captureSnapshot includes extra dashboard pages and unsaved drafts
    const snapshot = _captureSnapshot()
    const data = await api.post<{ preset: WorkspaceLayoutPreset }>(
      `/auth/tenants/${workspaceId}/layout-presets`,
      { name, description, layout: snapshot }
    )
    workspacePresets.value = [...workspacePresets.value, data.preset]
    return data.preset
  }

  async function deletePreset(workspaceId: string, presetId: string) {
    await api.delete(`/auth/tenants/${workspaceId}/layout-presets/${presetId}`)
    workspacePresets.value = workspacePresets.value.filter(p => p.id !== presetId)
  }

  async function applyPreset(preset: WorkspaceLayoutPreset) {
    await _applyLayout(preset.layout, 'workspace')
  }

  // ── Internal helpers ─────────────────────────────────────────────────────────

  /** Build a full LayoutPresetSnapshot from the current live store state. */
  function _captureSnapshot() {
    const { pages, getEffectiveWidgets } = useDashboardPages()
    const extraPageWidgets: Record<string, LayoutPresetWidget[]> = {}
    const presetPages: Array<{ pageId: string; slug: string; name: string; description?: string; widgets: LayoutPresetWidget[] }> = []
    for (const page of pages.value) {
      // Use getEffectiveWidgets so unsaved drafts are captured, not just the last API-persisted state.
      // Always store the array even if empty - an empty array is a valid state ("all widgets removed")
      // and must be preserved so that applying the preset restores the empty canvas.
      const widgets = getEffectiveWidgets(page.id)
      extraPageWidgets[page.id] = widgets
      presetPages.push({
        pageId: page.id,
        slug: page.slug,
        name: page.name,
        ...(page.description && { description: page.description }),
        widgets,
      })
    }
    return {
      mode: mode.value,
      sidebar: {
        position: sidebar.value.position,
        width: sidebar.value.width,
        collapsed: sidebar.value.collapsed,
      },
      splitView: {
        enabled: splitView.value.enabled,
        split: splitView.value.split,
      },
      dashboards: {
        classic: { widgets: classicDashboard.value.widgets },
        desktop: { widgets: desktopDashboard.value.widgets },
      },
      nav: {
        order: nav.value.order,
        hidden: nav.value.hidden,
      },
      ...(pages.value.length > 0 && { extraPageWidgets, presetPages }),
    }
  }

  /** Core apply logic shared by applyPreset. Syncs to backend immediately. */
  async function _applyLayout(
    layout: WorkspaceLayoutPreset['layout'],
    _source: 'workspace'
  ) {
    mode.value = layout.mode
    sidebar.value = { ...sidebar.value, ...layout.sidebar }
    splitView.value = { ...splitView.value, enabled: layout.splitView.enabled, split: layout.splitView.split }
    if (layout.dashboards) {
      if (layout.dashboards.classic.widgets.length) classicDashboard.value = { widgets: layout.dashboards.classic.widgets }
      if (layout.dashboards.desktop.widgets.length) desktopDashboard.value = { widgets: layout.dashboards.desktop.widgets }
    } else if (layout.dashboard) {
      // Backward compat: old presets that only stored one dashboard
      if (layout.mode === 'desktop') desktopDashboard.value = { widgets: layout.dashboard.widgets }
      else classicDashboard.value = { widgets: layout.dashboard.widgets }
    }
    // Apply extra dashboard pages - prefer presetPages (slug-based, supports cross-account)
    // over the legacy extraPageWidgets (ID-based, same-user only).
    // We collect an oldId→newId map so we can fix up dp_<id> entries in nav.order.
    let navOrderFromPreset = layout.nav?.order
    let navHiddenFromPreset = layout.nav?.hidden

    if (layout.presetPages && layout.presetPages.length > 0) {
      const { pages, createPage, clearAllPageDrafts } = useDashboardPages()
      const existingBySlug = new Map(pages.value.map(p => [p.slug, p]))
      // Map from the preset's original pageId → this account's pageId (for nav remap)
      const pageIdRemap = new Map<string, string>()

      for (const presetPage of layout.presetPages) {
        const existing = existingBySlug.get(presetPage.slug)
        if (existing) {
          // Page exists - update its widgets and record the remap
          pages.value = pages.value.map(p =>
            p.slug === presetPage.slug ? { ...p, widgets: presetPage.widgets } : p
          )
          pageIdRemap.set(presetPage.pageId, existing.id)
        } else {
          // Page missing - create it, then apply widgets
          try {
            const newPage = await createPage(presetPage.name, presetPage.slug, presetPage.description)
            pages.value = pages.value.map(p =>
              p.id === newPage.id ? { ...p, widgets: presetPage.widgets } : p
            )
            pageIdRemap.set(presetPage.pageId, newPage.id)
          } catch {
            // Non-fatal: skip if tier limit reached or slug conflict
          }
        }
      }

      // Remap dp_<oldPageId> → dp_<newPageId> in the preset's nav order so pages
      // appear in the correct position even after being re-created with new IDs.
      if (navOrderFromPreset && pageIdRemap.size > 0) {
        navOrderFromPreset = navOrderFromPreset.map(id => {
          if (id.startsWith('dp_')) {
            const oldPageId = id.slice(3)
            const newPageId = pageIdRemap.get(oldPageId)
            return newPageId ? `dp_${newPageId}` : id
          }
          return id
        })
      }
      if (navHiddenFromPreset && pageIdRemap.size > 0) {
        navHiddenFromPreset = navHiddenFromPreset.map(id => {
          if (id.startsWith('dp_')) {
            const oldPageId = id.slice(3)
            const newPageId = pageIdRemap.get(oldPageId)
            return newPageId ? `dp_${newPageId}` : id
          }
          return id
        })
      }

      clearAllPageDrafts()
    } else if (layout.extraPageWidgets && Object.keys(layout.extraPageWidgets).length > 0) {
      // Backward compat: old presets without presetPages (keyed by page ID)
      const { pages, clearAllPageDrafts } = useDashboardPages()
      pages.value = pages.value.map(page => {
        const widgets = layout.extraPageWidgets![page.id]
        return widgets ? { ...page, widgets } : page
      })
      clearAllPageDrafts()
    }

    // Apply nav after page remap so dp_ entries point to the right IDs
    if (navOrderFromPreset) nav.value = { order: navOrderFromPreset, hidden: navHiddenFromPreset ?? [] }

    save()
    // Write to backend immediately (not debounced) so there's no window where a
    // hydrateFromBackend call could race and overwrite the just-applied state.
    if (_syncTimer) { clearTimeout(_syncTimer); _syncTimer = null }
    await _syncToBackend()
  }

  // ── Header shortcuts ────────────────────────────────────────────────────────

  function getHeaderShortcuts(routeName: string): HeaderShortcut[] {
    return headerShortcuts.value[routeName] ?? []
  }

  function addHeaderShortcut(routeName: string, shortcut: Omit<HeaderShortcut, 'id'>) {
    const list = headerShortcuts.value[routeName] ?? []
    // Prevent duplicates for same token
    if (list.some(s => s.token === shortcut.token)) return
    headerShortcuts.value = {
      ...headerShortcuts.value,
      [routeName]: [...list, { ...shortcut, id: `${shortcut.type}__${shortcut.token}` }],
    }
    save()
    _scheduleBackendSync()
  }

  function removeHeaderShortcut(routeName: string, shortcutId: string) {
    const list = headerShortcuts.value[routeName] ?? []
    headerShortcuts.value = {
      ...headerShortcuts.value,
      [routeName]: list.filter(s => s.id !== shortcutId),
    }
    save()
    _scheduleBackendSync()
  }

  // Load on store init
  load()

  return {
    mode,
    sidebar,
    splitView,
    dashboard,
    nav,
    workspacePresets,
    presetsLoading,
    setMode,
    setSidebarCollapsed,
    setSidebarWidth,
    setSidebarPosition,
    setSplitViewEnabled,
    setSplitViewSplit,
    setSecondaryRoute,
    setSecondaryTitle,
    initDashboard,
    updateWidget,
    reorderWidgets,
    resetDashboard,
    saveDashboard,
    setNavOrder,
    hideNavItem,
    showNavItem,
    resetNav,
    fetchWorkspacePresets,
    publishPreset,
    deletePreset,
    applyPreset,
    headerShortcuts,
    getHeaderShortcuts,
    addHeaderShortcut,
    removeHeaderShortcut,
    hydrateFromBackend,
    flushBackendSync,
    resetForWorkspace,
    setDefaultPreset,
    clearForLogout,
    captureSnapshot: _captureSnapshot,
  }

  /**
   * Reset in-memory layout state and reload from the new workspace's localStorage.
   * Call this immediately after updating WORKSPACE_ID_KEY in localStorage (switchWorkspace).
   * hydrateFromBackend() still needs to run after navigation to get the full backend state.
   */
  function resetForWorkspace() {
    // Cancel pending sync - stale data from the old workspace shouldn't be written
    if (_syncTimer) { clearTimeout(_syncTimer); _syncTimer = null }
    _localDirty = false
    // Clear in-memory state to defaults
    mode.value = defaultState.mode
    sidebar.value = { ...defaultState.sidebar }
    splitView.value = { ...defaultState.splitView }
    classicDashboard.value = { widgets: [] }
    desktopDashboard.value = { widgets: [] }
    nav.value = { ...defaultState.nav, order: [...defaultState.nav.order], hidden: [] }
    headerShortcuts.value = {}
    workspacePresets.value = []
    // Reload from the new workspace's localStorage key (WORKSPACE_ID_KEY already updated)
    load()
  }

  /** Set or unset the default preset for new workspace members. */
  async function setDefaultPreset(workspaceId: string, presetId: string | null): Promise<void> {
    await api.patch(`/auth/tenants/${workspaceId}/layout-presets/${presetId ?? 'none'}/default`, {
      isDefault: presetId !== null,
    })
    // Reflect the change locally
    workspacePresets.value = workspacePresets.value.map(p => ({
      ...p,
      isDefault: p.id === presetId,
    }))
  }

  function clearForLogout() {
    mode.value = defaultState.mode
    sidebar.value = { ...defaultState.sidebar }
    splitView.value = { ...defaultState.splitView }
    classicDashboard.value = { widgets: [] }
    desktopDashboard.value = { widgets: [] }
    nav.value = { ...defaultState.nav, order: [...defaultState.nav.order], hidden: [] }
    headerShortcuts.value = {}
    workspacePresets.value = []
    // Reset dirty flag so next session's hydrateFromBackend isn't blocked
    _localDirty = false
    if (_syncTimer) { clearTimeout(_syncTimer); _syncTimer = null }
    // Remove only the current workspace's key - not all accounts' data
    const key = storageKey()
    if (key) localStorage.removeItem(key)
  }
})
